import { isPending, type MetricValue } from '@fleetcore/shared'

import { cn } from '../../lib/cn'
import { scaleFraction } from './geometry'
import { usePendingLabel } from './pending-label'

/**
 * A horizontal level bar — fuel in the tank, charge in the backup battery.
 *
 * A bar rather than a dial because the quantity is a proportion of something
 * physical: a reader compares it to "full", not to a range. The low-level marker
 * is drawn on the track rather than implied by colour alone, because the colour
 * change is the thing a red-green colour-blind dispatcher will not see.
 */

export interface LevelGaugeProps {
  label: string
  /** The level itself, on the [min,max] scale — a percentage unless told otherwise. */
  metric: MetricValue<number>
  format: (value: number) => string
  min?: number
  max?: number
  /** Value below which the bar reads as a warning. In the same units as `metric`. */
  lowThreshold?: number
  /** A second reading of the same quantity, e.g. litres beside a percentage. */
  secondary?: string | null
  hint?: string
  /** Resolved sentence replacing the generic pending wording — see <ArcGauge />. */
  pendingText?: string
  className?: string
}

export function LevelGauge({
  label,
  metric,
  format,
  min = 0,
  max = 100,
  lowThreshold,
  secondary,
  hint,
  pendingText,
  className,
}: LevelGaugeProps) {
  const pendingLabel = usePendingLabel()
  const pending = isPending(metric)

  const fraction = pending ? 0 : scaleFraction(metric.value, min, max)
  const valueText = pending ? (pendingText ?? pendingLabel(metric)) : format(metric.value)
  const low = !pending && lowThreshold !== undefined && metric.value <= lowThreshold

  return (
    <div className={cn('flex min-w-0 flex-col gap-1.5', className)}>
      <div className="flex items-baseline justify-between gap-2">
        <p className="truncate text-xs font-medium text-foreground">{label}</p>
        <p
          className={cn(
            'text-xs tabular-nums',
            // A resolved absence is a sentence, not a reading: it needs room to wrap
            // rather than the no-shrink column a number sits in.
            pending ? 'min-w-0 text-end' : 'shrink-0',
            pending
              ? 'text-foreground-subtle'
              : low
                ? 'font-semibold text-danger'
                : 'text-foreground',
          )}
        >
          {valueText}
          {!pending && secondary ? (
            <span className="ms-1 font-normal text-foreground-subtle">{secondary}</span>
          ) : null}
        </p>
      </div>

      <svg
        role="img"
        aria-label={`${label} : ${valueText}`}
        viewBox="0 0 100 10"
        preserveAspectRatio="none"
        className="h-2.5 w-full"
      >
        <rect
          x={0}
          y={0}
          width={100}
          height={10}
          rx={5}
          className="fill-current text-surface-muted"
        />

        {!pending ? (
          <rect
            x={0}
            y={0}
            width={Math.max(fraction * 100, fraction > 0 ? 2 : 0)}
            height={10}
            rx={5}
            className={cn('fill-current', low ? 'text-danger' : 'text-accent')}
          />
        ) : null}

        {lowThreshold !== undefined ? (
          <line
            x1={scaleFraction(lowThreshold, min, max) * 100}
            y1={0}
            x2={scaleFraction(lowThreshold, min, max) * 100}
            y2={10}
            stroke="currentColor"
            strokeWidth={1.5}
            className="text-border-strong"
          />
        ) : null}
      </svg>

      {hint ? <p className="truncate text-xs text-foreground-subtle">{hint}</p> : null}
    </div>
  )
}
